import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Plus, X, Search, Loader2, AlertCircle, Package,
  Warehouse, BarChart2, ChevronDown, CheckCircle, ArrowRight
} from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';

/* ──────────────────────────────────────────────
   CONSTANTS
   ────────────────────────────────────────────── */
const ADJUSTMENT_TYPES = [
  { value: 'Increase', label: 'Increase', icon: '+', color: 'emerald' },
  { value: 'Decrease', label: 'Decrease', icon: '−', color: 'red' },
  { value: 'Physical Count', label: 'Physical Count', icon: '±', color: 'slate' },
];

/* ──────────────────────────────────────────────
   TYPE PILL
   ────────────────────────────────────────────── */
const TypePill = ({ type, selected, onClick }) => {
  const base = 'flex items-center gap-1.5 px-3.5 py-2 rounded-lg border text-sm font-semibold cursor-pointer transition-all ';
  const colors = {
    emerald: selected
      ? 'bg-emerald-600 border-emerald-600 text-white shadow-md'
      : 'bg-emerald-50 border-emerald-200 text-emerald-700 hover:border-emerald-400',
    red: selected
      ? 'bg-red-600 border-red-600 text-white shadow-md'
      : 'bg-red-50 border-red-200 text-red-700 hover:border-red-400',
    slate: selected
      ? 'bg-slate-600 border-slate-600 text-white shadow-md'
      : 'bg-slate-100 border-slate-300 text-slate-700 hover:border-slate-400',
  };
  return (
    <button type="button" onClick={onClick} className={base + colors[type.color]}>
      <span className="text-base leading-none">{type.icon}</span>
      {type.label}
    </button>
  );
};

/* ──────────────────────────────────────────────
   LIVE PREVIEW BAR
   ────────────────────────────────────────────── */
const PreviewBar = ({ before, adjQty, after, adjType }) => {
  const isIncrease = adjType === 'Increase';
  const isDecrease = adjType === 'Decrease';
  const isPhysical = adjType === 'Physical Count';

  const adjColor = isIncrease ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
    : isDecrease ? 'bg-red-50 text-red-700 border-red-200'
      : 'bg-slate-100 text-slate-700 border-slate-300';

  const sign = isIncrease ? '+' : isDecrease ? '−' : '→';

  return (
    <div className="flex items-center justify-center gap-3 bg-gray-50 border border-gray-200 rounded-xl px-5 py-4">
      <div className="text-center">
        <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">Before</p>
        <p className="text-2xl font-bold text-gray-700">{before ?? '—'}</p>
      </div>

      <ArrowRight className="w-5 h-5 text-gray-300 flex-shrink-0" />

      <div className={`text-center px-3 py-1.5 rounded-lg border ${adjColor}`}>
        <p className="text-xs font-semibold uppercase tracking-wide opacity-70 mb-0.5">Adj</p>
        <p className="text-xl font-bold">
          {sign}{isPhysical ? (adjQty ?? '—') : (adjQty ?? '—')}
        </p>
      </div>

      <ArrowRight className="w-5 h-5 text-gray-300 flex-shrink-0" />

      <div className="text-center">
        <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">After</p>
        <p className={`text-2xl font-bold ${after !== null && after < 0 ? 'text-red-600' : 'text-teal-700'}`}>
          {after ?? '—'}
        </p>
      </div>
    </div>
  );
};

/* ══════════════════════════════════════════════
   MAIN
   ══════════════════════════════════════════════ */
export default function StockAdjustmentForm({ isModal = false, onClose, onSuccess }) {
  const [inventoryData, setInventoryData] = useState([]);
  const [loadingInv, setLoadingInv] = useState(true);
  const [invError, setInvError] = useState(null);

  const [searchText, setSearchText] = useState('');
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const pickerRef = useRef(null);

  const [adjType, setAdjType] = useState('Increase');
  const [adjQty, setAdjQty] = useState('');
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [adjustedBy, setAdjustedBy] = useState(1);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoadingInv(true);
      setInvError(null);
      try {
        const res = await fetch(`${API_BASE_URL}api/InventoryData`);
        if (!res.ok) throw new Error(`Server error ${res.status}`);
        const data = await res.json();
        setInventoryData(Array.isArray(data) ? data : []);
      } catch (e) {
        setInvError(e.message || 'Failed to load inventory');
      } finally {
        setLoadingInv(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const pickerOptions = useMemo(() => {
    if (!searchText.trim()) return inventoryData;
    const q = searchText.toLowerCase();
    return inventoryData.filter(
      (r) =>
        r.itemCode?.toLowerCase().includes(q) ||
        r.itemDescription?.toLowerCase().includes(q) ||
        r.warehouseCode?.toLowerCase().includes(q) ||
        r.batchNo?.toLowerCase().includes(q)
    );
  }, [inventoryData, searchText]);

  const currentQty = selected ? Number(selected.quantity) || 0 : null;
  const numQty = Number(adjQty) || 0;

  const previewAfter = useMemo(() => {
    if (currentQty === null || !adjQty) return null;
    if (adjType === 'Increase') return currentQty + numQty;
    if (adjType === 'Decrease') return currentQty - numQty;
    if (adjType === 'Physical Count') return numQty;
    return null;
  }, [currentQty, adjQty, adjType, numQty]);

  const validationErrors = useMemo(() => {
    const errs = [];
    if (!selected) errs.push('Select an inventory item.');
    if (!adjQty || numQty <= 0) errs.push('Enter a quantity greater than 0.');
    if (adjType === 'Decrease' && previewAfter !== null && previewAfter < 0)
      errs.push('Adjustment would result in negative stock.');
    return errs;
  }, [selected, adjQty, numQty, adjType, previewAfter]);

  const isValid = validationErrors.length === 0;

  const handleSubmit = async () => {
    if (!isValid || submitting) return;
    setSubmitting(true);
    try {
      // 🔥 AUTO-GENERATE ADJUSTMENT NUMBER: YYYYMMDDHHMMSS format
      const now = new Date();
      const adjustmentNo =
        now.getFullYear().toString() +
        (now.getMonth() + 1).toString().padStart(2, '0') +
        now.getDate().toString().padStart(2, '0') +
        now.getHours().toString().padStart(2, '0') +
        now.getMinutes().toString().padStart(2, '0') +
        now.getSeconds().toString().padStart(2, '0');

      const payload = {
        adjustmentNo: adjustmentNo,  // ✅ NOW INCLUDED!
        itemCode: selected.itemCode,
        itemDescription: selected.itemDescription,
        warehouseCode: selected.warehouseCode,
        warehouseId: selected.warehouseId || null,
        batchNo: selected.batchNo || '',
        adjustmentType: adjType,
        adjustmentQty: adjType === 'Physical Count' ? numQty : numQty,
        uom: selected.uom || 'pcs',
        reason: reason.trim() || '',
        remarks: remarks.trim() || '',
        adjustedBy: adjustedBy,
        adjustedByName: 'Admin User',
        status: 1,
      };

      // 🔍 LOG THE PAYLOAD BEFORE SENDING
      console.log('📦 SENDING PAYLOAD:', payload);
      console.log('📦 PAYLOAD AS JSON:', JSON.stringify(payload, null, 2));

      const res = await fetch(`${API_BASE_URL}api/StockAdjustment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      // 🔍 LOG THE RESPONSE STATUS
      console.log('📡 RESPONSE STATUS:', res.status);
      console.log('📡 RESPONSE OK:', res.ok);

      if (!res.ok) {
        // 🔍 TRY TO GET THE FULL ERROR RESPONSE
        const responseText = await res.text();
        console.error('❌ RAW ERROR RESPONSE:', responseText);

        let errData = null;
        try {
          errData = JSON.parse(responseText);
          console.error('❌ PARSED ERROR DATA:', errData);
        } catch (parseErr) {
          console.error('❌ ERROR PARSING RESPONSE:', parseErr);
        }

        // Log everything we can
        console.error('❌ ERROR DETAILS:', {
          status: res.status,
          statusText: res.statusText,
          headers: Object.fromEntries(res.headers.entries()),
          errorData: errData,
          rawResponse: responseText
        });

        throw new Error(errData?.message || errData?.title || `Failed to create adjustment (${res.status})`);
      }

      // 🔍 LOG SUCCESS RESPONSE
      const successData = await res.json();
      console.log('✅ SUCCESS RESPONSE:', successData);

      const user = JSON.parse(localStorage.getItem("user"));
      await fetch(`${API_BASE_URL}api/Logs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "UPDATE",
          module: "Inventory",
          description: `Stock Adjustment - Item: ${selected.itemCode}, Type: ${adjType}, Qty: ${numQty}, Warehouse: ${selected.warehouseCode}. Adj#: ${adjustmentNo}`,
          userId: user?.userId,
          username: user?.username,
          fullName: user?.fullName || user?.username,
        })
      }).catch(err => console.error("Log failed:", err));
      
      await Swal.fire({
        icon: 'success',
        title: 'Adjustment Created!',
        html: `<strong>${adjType}</strong> of <strong>${numQty}</strong> unit(s) recorded for <strong>${selected.itemCode}</strong>.<br><small style="color:#6b7280">Adj#: ${adjustmentNo}<br>Stock: ${currentQty} → ${previewAfter}</small>`,
        confirmButtonColor: '#475569',
        timer: 2500,
        showConfirmButton: false,
      });

      setSelected(null);
      setSearchText('');
      setAdjType('Increase');
      setAdjQty('');
      setReason('');
      setRemarks('');

      if (onSuccess) onSuccess();
      if (isModal && onClose) onClose();

    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: err.message, confirmButtonColor: '#dc2626' });
    } finally {
      setSubmitting(false);
    }
  };

  const renderPicker = () => (
    <div ref={pickerRef} className="relative">
      <div
        onClick={() => setPickerOpen(true)}
        className="flex items-center gap-2 w-full px-3 py-2.5 border border-gray-300 rounded-lg bg-white cursor-pointer hover:border-slate-400 transition focus-within:ring-2 focus-within:ring-slate-300"
      >
        <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <input
          type="text"
          autoComplete="off"
          placeholder="Search item code / description…"
          value={selected ? `${selected.itemCode} — ${selected.itemDescription || ''}` : searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setSelected(null);
            setPickerOpen(true);
          }}
          onFocus={() => setPickerOpen(true)}
          className="flex-1 text-sm outline-none bg-transparent text-gray-800 placeholder-gray-400 min-w-0"
        />
        {selected && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setSelected(null); setSearchText(''); }}
            className="text-gray-400 hover:text-gray-600 transition"
          >
            <X className="w-4 h-4" />
          </button>
        )}
        {!selected && <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${pickerOpen ? 'rotate-180' : ''}`} />}
      </div>

      {pickerOpen && !selected && (
        <div className="absolute z-30 mt-1.5 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-64 overflow-y-auto">
          {loadingInv && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 text-slate-500 animate-spin" />
            </div>
          )}
          {!loadingInv && invError && (
            <div className="flex items-center gap-2 px-4 py-3 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" /> {invError}
            </div>
          )}
          {!loadingInv && !invError && pickerOptions.length === 0 && (
            <p className="px-4 py-3 text-sm text-gray-400 text-center">No items found.</p>
          )}
          {!loadingInv && !invError && pickerOptions.map((row, i) => (
            <button
              type="button"
              key={row.id ?? i}
              onClick={() => { setSelected(row); setPickerOpen(false); }}
              className="w-full text-left px-4 py-2.5 hover:bg-slate-50 transition border-b border-gray-100 last:border-0"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5 min-w-0">
                  <span className="font-mono font-semibold text-slate-800 text-sm">{row.itemCode}</span>
                  <span className="text-gray-500 text-sm truncate">{row.itemDescription}</span>
                </div>
                <span className="text-xs font-bold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full flex-shrink-0 ml-2 border border-teal-200">
                  {row.quantity} {row.uom || 'pcs'}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-0.5 ml-0">
                {row.warehouseCode && (
                  <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                    <Warehouse className="w-3 h-3" />{row.warehouseCode}
                  </span>
                )}
                {row.batchNo && (
                  <span className="inline-flex items-center gap-1 text-xs text-slate-600">
                    <Package className="w-3 h-3" />Batch: {row.batchNo}
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const renderSelectedInfo = () => {
    if (!selected) return null;
    return (
      <div className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-lg px-4 py-3 mt-3">
        <div className="flex items-center gap-3">
          <div className="bg-slate-100 rounded-lg p-2 border border-slate-300">
            <Package className="w-5 h-5 text-slate-600" />
          </div>
          <div>
            <p className="font-mono font-bold text-slate-800">{selected.itemCode}</p>
            <p className="text-xs text-gray-500">{selected.itemDescription}</p>
          </div>
        </div>
        <div className="flex items-center gap-3 text-right">
          {selected.warehouseCode && (
            <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md text-xs font-semibold border border-slate-200">
              <Warehouse className="w-3 h-3" />{selected.warehouseCode}
            </span>
          )}
          {selected.batchNo && (
            <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-xs font-semibold border border-slate-200">
              Batch: {selected.batchNo}
            </span>
          )}
          <span className="text-sm font-bold text-teal-700">
            Qty: {selected.quantity}
          </span>
        </div>
      </div>
    );
  };

  const formContent = (
    <div className="flex flex-col gap-5">

      <div>
        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
          Select Item *
        </label>
        {renderPicker()}
        {renderSelectedInfo()}
      </div>

      <div>
        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-2">
          Adjustment Type *
        </label>
        <div className="flex gap-2 flex-wrap">
          {ADJUSTMENT_TYPES.map((t) => (
            <TypePill
              key={t.value}
              type={t}
              selected={adjType === t.value}
              onClick={() => { setAdjType(t.value); setAdjQty(''); }}
            />
          ))}
        </div>
      </div>

      <div>
        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
          {adjType === 'Physical Count' ? 'Actual Count *' : 'Adjustment Qty *'}
        </label>
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <span className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm font-bold ${adjType === 'Increase' ? 'text-emerald-600' :
                adjType === 'Decrease' ? 'text-red-600' : 'text-slate-600'
              }`}>
              {adjType === 'Increase' ? '+' : adjType === 'Decrease' ? '−' : '='}
            </span>
            <input
              type="number"
              min="0"
              step="any"
              placeholder="0"
              value={adjQty}
              onChange={(e) => setAdjQty(e.target.value)}
              className="w-full pl-7 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 transition"
            />
          </div>
          <span className="text-xs text-gray-500 font-semibold">{selected?.uom || 'pcs'}</span>
        </div>
        {adjType === 'Decrease' && previewAfter !== null && previewAfter < 0 && (
          <p className="mt-1.5 text-xs text-red-600 font-semibold flex items-center gap-1">
            <AlertCircle className="w-3.5 h-3.5" /> This would result in negative stock.
          </p>
        )}
      </div>

      {selected && (
        <div>
          <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-2">Preview</label>
          <PreviewBar
            before={currentQty}
            adjQty={adjQty || 0}
            after={previewAfter}
            adjType={adjType}
          />
        </div>
      )}

      <div>
        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
          Reason
        </label>
        <input
          type="text"
          placeholder="e.g. Damaged goods, Recount, …"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 transition"
        />
      </div>

      <div>
        <label className="block text-xs font-bold text-slate-700 uppercase tracking-wide mb-1.5">
          Remarks
        </label>
        <textarea
          rows={2}
          placeholder="Additional notes…"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 transition resize-none"
        />
      </div>
    </div>
  );

  if (isModal) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col">

          <div className="bg-gradient-to-r from-slate-700 to-slate-600 text-white px-6 py-4 flex-shrink-0 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/15 rounded-lg p-2"><BarChart2 className="w-5 h-5" /></div>
              <div>
                <h3 className="text-base font-bold">New Stock Adjustment</h3>
                <p className="text-slate-300 text-xs">Increase, decrease, or set actual count</p>
              </div>
            </div>
            <button onClick={onClose} className="bg-white/10 hover:bg-white/20 rounded-lg p-1.5 transition">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {formContent}
          </div>

          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700 font-medium transition">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!isValid || submitting}
              className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2 rounded-lg shadow-sm transition"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              {submitting ? 'Saving…' : 'Submit Adjustment'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-lg mx-auto">

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-5">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart2 className="w-6 h-6 text-slate-600" />
            New Stock Adjustment
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Increase, decrease, or set an actual physical count.</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {formContent}
        </div>

        <div className="mt-5 flex items-center justify-end">
          <button
            onClick={handleSubmit}
            disabled={!isValid || submitting}
            className="inline-flex items-center gap-2 bg-slate-700 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-semibold px-6 py-2.5 rounded-lg shadow-sm transition"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
            {submitting ? 'Saving…' : 'Submit Adjustment'}
          </button>
        </div>
      </div>
    </div>
  );
}